/**
 * ProjectDetail — dossier: context, begrotingsposten, tekening-voorstel, totalen, export
 * Visuele taal: dashboard / onboarding / nieuw dossier.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, FolderKanban, Loader2, Pencil, X } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import type { SystemSelection } from '../components/ManualSystemPicker';
import { ProjectJourneySteps } from '../components/ProjectJourneySteps';
import { SeoMeta } from '../components/SeoMeta';
import { api } from '../lib/api';
import { trackOfferteGenerated } from '../lib/analytics';
import { CUSTOMER_TYPE_OPTIONS, DEFAULT_CALC_INDEX_YEAR } from '../lib/customer-types';
import { getCustomerUxProfile } from '../lib/customer-profiles';
import { getJourneyProfileHint } from '../lib/project-flow-copy';
import type {
  ApproveDrawingProposalMode,
  ProjectRecord,
  ProjectSystemLine,
  QuoteDocument,
} from '../types/project';
import type { ProjectDetailOutletContext } from './project-detail/outletTypes';
import type { AggregationResult } from './project-detail/types';
import { normalizeAggregationApiResponse } from './project-detail/aggregation-normalize';
import { useUndoRedo } from '../hooks/useUndoRedo';
import { useAuth } from '../lib/AuthContext';

function newBoqLineId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `line_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function projectSystemsToDraft(systems: ProjectSystemLine[] | undefined): SystemSelection[] {
  return (systems ?? []).map((s) => ({
    line_id: s.line_id ?? newBoqLineId(),
    system_id: s.system_id,
    variant_id: s.variant_id,
    quantity: s.quantity,
    building_part: s.building_part ?? '',
    floor: s.floor ?? '',
    room_number: s.room_number ?? '',
  }));
}

function normalizePostalCode(raw: string): string {
  return raw.replace(/\s+/g, '').toUpperCase();
}

const inputClass =
  'w-full rounded-lg border border-border px-3 py-2 text-text focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';
const btnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary-alt disabled:opacity-50';
const btnPrimaryLg =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-white shadow-md transition hover:bg-primary-alt disabled:opacity-50';
const btnSecondary =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-surface-card px-5 py-2.5 text-sm font-medium text-text transition hover:bg-surface disabled:opacity-50';

const TAB_BASE =
  'rounded-lg px-3.5 py-2 text-sm font-medium transition';

function DossierTabLink({
  to,
  end,
  disabled,
  children,
}: {
  to: string;
  end?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  if (disabled) {
    return (
      <span
        className={`${TAB_BASE} cursor-not-allowed text-text-muted/45`}
        title="Sla minstens één begrotingsregel op bij Posten (knop ‘Regels opslaan’) om Resultaat en Rapportage te openen."
      >
        {children}
      </span>
    );
  }
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `${TAB_BASE} ${isActive ? 'bg-primary text-white shadow-sm' : 'text-text-muted hover:bg-surface hover:text-text'}`
      }
    >
      {children}
    </NavLink>
  );
}

export function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const aggregationRequestSeq = useRef(0);
  const [aggregation, setAggregation] = useState<AggregationResult | null>(null);
  const [aggregationLoading, setAggregationLoading] = useState(true);
  const [generatedDoc, setGeneratedDoc] = useState<QuoteDocument | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [proposalRows, setProposalRows] = useState<ProjectSystemLine[] | null>(null);
  const [approving, setApproving] = useState(false);
  const [contextSaving, setContextSaving] = useState(false);
  const [boqSaving, setBoqSaving] = useState(false);
  const {
    state: boqDraft,
    setState: setBoqDraft,
    undo: boqUndo,
    redo: boqRedo,
    canUndo: boqCanUndo,
    canRedo: boqCanRedo,
    reset: resetBoqDraft,
  } = useUndoRedo<SystemSelection[]>([]);
  const [projectType, setProjectType] = useState('renovatie');
  const [buildingType, setBuildingType] = useState('woning');
  const [postalCode, setPostalCode] = useState('');
  const [regionId, setRegionId] = useState('');
  const [customerType, setCustomerType] = useState('');
  /** null = eerste regel standaard uitgeklapt; -1 = alles dicht; ≥0 = die index open */
  const [expandedBreakdown, setExpandedBreakdown] = useState<number | null>(null);
  const [aggregationError, setAggregationError] = useState<string | null>(null);
  const [flowError, setFlowError] = useState<string | null>(null);
  const [contextExpanded, setContextExpanded] = useState(false);
  const [regionSuggestLoading, setRegionSuggestLoading] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFeedback, setExportFeedback] = useState<string | null>(null);
  const [exportVatRate, setExportVatRate] = useState(0.21);
  const [exportWasteFactor, setExportWasteFactor] = useState(1.05);
  const [exportCsvDelimiter, setExportCsvDelimiter] = useState<';' | ','>(';');
  const [exportIncludeDisclaimer, setExportIncludeDisclaimer] = useState(true);
  const [exportIncludePerDeelBegroting, setExportIncludePerDeelBegroting] = useState(true);
  const [exportIncludeCostSummary, setExportIncludeCostSummary] = useState(true);
  const [exportIncludeRegeldetail, setExportIncludeRegeldetail] = useState(false);
  const [exportIncludeCo2, setExportIncludeCo2] = useState(false);
  const [loadingDocPdf, setLoadingDocPdf] = useState(false);
  const [nameEditing, setNameEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameCopyFlash, setNameCopyFlash] = useState(false);

  const [projectTypeOptions, setProjectTypeOptions] = useState<Array<{ id: string; label: string }>>([
    { id: 'renovatie', label: 'Renovatie' },
  ]);
  const [buildingTypeOptions, setBuildingTypeOptions] = useState<Array<{ id: string; label: string }>>([
    { id: 'woning', label: 'Woning' },
  ]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesLoadError, setTypesLoadError] = useState<string | null>(null);

  const loadReferenceTypes = useCallback(async () => {
    setTypesLoading(true);
    setTypesLoadError(null);
    try {
      const [pt, bt] = await Promise.all([api.getProjectTypes(), api.getBuildingTypes()]);
      if (pt.project_types?.length) setProjectTypeOptions(pt.project_types);
      if (bt.building_types?.length) setBuildingTypeOptions(bt.building_types);
    } catch {
      setTypesLoadError('Project- en gebouwtypes konden niet worden geladen. Je kunt nog steeds handmatig opslaan.');
    } finally {
      setTypesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReferenceTypes();
  }, [loadReferenceTypes]);

  const tenantIdForFetch = user?.tenant_id ?? 'default';
  const { data: project, isLoading: loadingProject, error } = useQuery({
    queryKey: ['project', projectId, tenantIdForFetch],
    queryFn: () => api.getProject(projectId!, tenantIdForFetch),
    enabled: !!projectId && !!user?.authenticated,
  });

  const proj = project as ProjectRecord | undefined;
  const tenantIdForApi = proj?.tenant_id ?? tenantIdForFetch;
  const indexForPricing = proj?.context?.index_year ?? DEFAULT_CALC_INDEX_YEAR;

  const { data: laborRatesData } = useQuery({
    queryKey: ['tenant-labor-rates', tenantIdForApi],
    queryFn: () => api.getTenantLaborRates(tenantIdForApi),
    enabled: !!user?.authenticated,
    staleTime: 60_000,
  });

  const laborRatesFingerprint = useMemo(() => {
    if (!laborRatesData?.profiles?.length) return '';
    return laborRatesData.profiles
      .map((p) => `${p.labor_profile_id}:${p.override_hourly_rate ?? ''}`)
      .join('|');
  }, [laborRatesData]);

  const projectTypeOptionsMerged = useMemo(() => {
    const opts = [...projectTypeOptions];
    if (projectType && !opts.some((o) => o.id === projectType)) {
      opts.unshift({ id: projectType, label: projectType });
    }
    return opts;
  }, [projectTypeOptions, projectType]);

  const buildingTypeOptionsMerged = useMemo(() => {
    const opts = [...buildingTypeOptions];
    if (buildingType && !opts.some((o) => o.id === buildingType)) {
      opts.unshift({ id: buildingType, label: buildingType });
    }
    return opts;
  }, [buildingTypeOptions, buildingType]);

  const projectTypeLabel = useMemo(
    () => projectTypeOptionsMerged.find((o) => o.id === projectType)?.label ?? projectType,
    [projectTypeOptionsMerged, projectType]
  );
  const buildingTypeLabel = useMemo(
    () => buildingTypeOptionsMerged.find((o) => o.id === buildingType)?.label ?? buildingType,
    [buildingTypeOptionsMerged, buildingType]
  );

  const { data: regionsData } = useQuery({
    queryKey: ['regions'],
    queryFn: () => api.getRegions(),
    staleTime: 60 * 60 * 1000,
  });

  useEffect(() => {
    if (!proj) return;
    setProjectType(proj.project_type ?? 'renovatie');
    setBuildingType(proj.building_type ?? 'woning');
    setPostalCode(proj.context?.postal_code ?? '');
    setRegionId(proj.context?.region_id ?? '');
    setCustomerType(proj.context?.customer_type ?? 'particulier');
  }, [proj?.project_id, proj?.project_type, proj?.building_type, proj?.context]);

  /** Geldige postcode + ontbrekende of gewijzigde regio op server → suggestie vastleggen (debounce). */
  useEffect(() => {
    if (!projectId || !proj?.project_id) return;
    const pc = normalizePostalCode(postalCode);
    if (!pc || !/^\d{4}[A-Z]{2}$/.test(pc)) return;
    const savedPc = normalizePostalCode(proj.context?.postal_code ?? '');
    const savedRid = (proj.context?.region_id ?? '').trim();
    if (pc === savedPc && savedRid) return;

    const t = window.setTimeout(() => {
      void (async () => {
        setRegionSuggestLoading(true);
        setFlowError(null);
        try {
          const s = await api.suggestRegionFromPostalCode(pc);
          setRegionId(s.region_id);
          const fresh = queryClient.getQueryData<ProjectRecord>(['project', projectId]);
          if (!fresh?.project_id) return;
          await api.updateProject(
            projectId,
            {
              tenant_id: fresh.tenant_id ?? tenantIdForApi,
              context: {
                ...fresh.context,
                postal_code: pc,
                region_id: s.region_id,
                index_year: fresh.context?.index_year ?? DEFAULT_CALC_INDEX_YEAR,
                customer_type: fresh.context?.customer_type ?? 'particulier',
              },
            },
            { revisionSeq: fresh.revision_seq ?? 1 }
          );
          await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
        } catch (e) {
          setFlowError(e instanceof Error ? e.message : 'Regio kon niet automatisch worden bepaald.');
        } finally {
          setRegionSuggestLoading(false);
        }
      })();
    }, 500);
    return () => window.clearTimeout(t);
  }, [
    postalCode,
    proj?.project_id,
    proj?.revision_seq,
    proj?.context?.postal_code,
    proj?.context?.region_id,
    proj?.context?.index_year,
    proj?.context?.customer_type,
    projectId,
    tenantIdForApi,
    queryClient,
  ]);

  useEffect(() => {
    if (proj?.drawing_proposal?.status === 'draft' && proj.drawing_proposal.proposed_systems?.length) {
      setProposalRows(proj.drawing_proposal.proposed_systems.map((s) => ({ ...s })));
    } else {
      setProposalRows(null);
    }
  }, [proj?.drawing_proposal]);

  const systemsFingerprint = useMemo(() => JSON.stringify(proj?.systems ?? []), [proj?.systems]);

  useEffect(() => {
    if (!proj?.project_id) return;
    resetBoqDraft(projectSystemsToDraft(proj.systems));
    // Alleen hertalen bij project-id of inhoud systems[] (niet bij elke query-refetch zelfde data)
  }, [proj?.project_id, systemsFingerprint, resetBoqDraft]);

  const systemsForAgg = proj?.systems;
  const hasSystems = (systemsForAgg?.length ?? 0) > 0;
  const aggSystemsKey = useMemo(() => JSON.stringify(systemsForAgg ?? []), [systemsForAgg]);
  const contextRegionId = proj?.context?.region_id ?? '';
  const contextIndexYear = proj?.context?.index_year ?? '';
  /** Draft-voorstel met regels: altijd keuren op Posten; bij bestaande posten kies je vervangen / toevoegen / samenvoegen. */
  const pendingDrawingApproval =
    proj?.drawing_proposal?.status === 'draft' && (proposalRows?.length ?? 0) > 0;

  const savedSystemsCount = proj?.systems?.length ?? 0;
  const canAccessResultTabs = savedSystemsCount > 0;

  /** Altijd 1–4 gelijk aan tabs (gegevens/posten/resultaat/rapportage) — één mentale route. */
  const journeyTabStep = useMemo(() => {
    const tail = location.pathname.split('/').pop() ?? '';
    if (tail === 'gegevens') return 1;
    if (tail === 'posten') return 2;
    if (tail === 'resultaat') return 3;
    if (tail === 'rapportage') return 4;
    return 1;
  }, [location.pathname]);

  const dossierJourneyTabLinks = useMemo(
    () =>
      projectId
        ? [
            { to: `/projects/${projectId}/gegevens`, end: true },
            { to: `/projects/${projectId}/posten` },
            { to: `/projects/${projectId}/resultaat`, disabled: !canAccessResultTabs },
            { to: `/projects/${projectId}/rapportage`, disabled: !canAccessResultTabs },
          ]
        : undefined,
    [projectId, canAccessResultTabs]
  );

  const customerUxProfile = useMemo(
    () => getCustomerUxProfile(customerType || 'particulier'),
    [customerType]
  );

  const detailProfileHint = useMemo(() => {
    if (!proj) return undefined;
    const ctLabel =
      CUSTOMER_TYPE_OPTIONS.find((o) => o.id === customerType)?.label ?? customerType;
    return getJourneyProfileHint({
      projectTypeId: projectType,
      buildingTypeId: buildingType,
      customerTypeId: customerType,
      projectTypeLabel,
      buildingTypeLabel,
      customerTypeLabel: ctLabel,
    });
  }, [proj, projectType, buildingType, customerType, projectTypeLabel, buildingTypeLabel]);

  useEffect(() => {
    if (!proj?.project_id || !hasSystems) {
      setAggregationLoading(false);
      setAggregation(null);
      setAggregationError(null);
      return;
    }

    const systems = proj.systems ?? [];
    if (systems.length === 0) {
      setAggregationLoading(false);
      setAggregation(null);
      setAggregationError(null);
      return;
    }

    const seq = ++aggregationRequestSeq.current;
    setAggregationLoading(true);
    setAggregationError(null);
    setAggregation(null);

    api
      .aggregateProject({
        project_id: proj.project_id,
        project_type: proj.project_type ?? 'renovatie',
        building_type: proj.building_type ?? 'woning',
        systems: systems.map((s) => ({
          system_id: s.system_id,
          variant_id: s.variant_id,
          quantity: s.quantity,
          ...(s.building_part?.trim() ? { building_part: s.building_part.trim() } : {}),
          ...(s.floor?.trim() ? { floor: s.floor.trim() } : {}),
          ...(s.room_number?.trim() ? { room_number: s.room_number.trim() } : {}),
        })),
        tenant_id: tenantIdForApi,
        index_year: indexForPricing,
        region_id: proj.context?.region_id || undefined,
      })
      .then((res) => {
        if (seq !== aggregationRequestSeq.current) return;
        const raw = res as Record<string, unknown>;
        const normalized = normalizeAggregationApiResponse(raw, proj.project_id);
        const systemsWithLineIds = {
          ...normalized,
          systems: normalized.systems.map((s, i) => ({
            ...s,
            line_id: s.line_id ?? proj.systems?.[i]?.line_id ?? undefined,
          })),
        };
        setAggregation(systemsWithLineIds);
        setAggregationError(null);
      })
      .catch((e) => {
        if (seq !== aggregationRequestSeq.current) return;
        setAggregation(null);
        setAggregationError(e instanceof Error ? e.message : 'Aggregatie mislukt. Controleer netwerk en catalogus.');
      })
      .finally(() => {
        if (seq === aggregationRequestSeq.current) setAggregationLoading(false);
      });
  }, [
    proj?.project_id,
    proj?.project_type,
    proj?.building_type,
    hasSystems,
    aggSystemsKey,
    tenantIdForApi,
    indexForPricing,
    contextRegionId,
    contextIndexYear,
    laborRatesFingerprint,
  ]);

  const handleExport = async (format: 'csv' | 'excel' | 'pdf') => {
    if (!aggregation || !proj) return;
    setExporting(true);
    setFlowError(null);
    try {
      const apiFormat = format === 'excel' ? 'csv' : format;
      const blob = await api.exportProject({
        project_id: aggregation.project_id,
        systems: aggregation.systems.map((s, i: number) => {
          const line = proj.systems?.[i];
          return {
            system_id: s.system_id,
            variant_id: s.variant_id,
            quantity: s.quantity,
            ...(line?.building_part?.trim() ? { building_part: line.building_part.trim() } : {}),
            ...(line?.floor?.trim() ? { floor: line.floor.trim() } : {}),
            ...(line?.room_number?.trim() ? { room_number: line.room_number.trim() } : {}),
          };
        }),
        tenant_id: proj.tenant_id ?? 'default',
        project_type: proj.project_type ?? 'renovatie',
        building_type: proj.building_type ?? 'woning',
        index_year: proj.context?.index_year ?? DEFAULT_CALC_INDEX_YEAR,
        region_id: proj.context?.region_id || undefined,
        format: apiFormat,
        vat_rate: exportVatRate,
        waste_factor: exportWasteFactor,
        csv_delimiter: exportCsvDelimiter,
        include_export_disclaimer: exportIncludeDisclaimer,
        include_per_deel_begroting: exportIncludePerDeelBegroting,
        include_cost_summary: exportIncludeCostSummary,
        include_regel_detail: exportIncludeRegeldetail,
        include_co2: exportIncludeCo2,
        project_label: proj.metadata?.name?.trim() || displayName || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = globalThis.document.createElement('a');
      a.href = url;
      a.download =
        format === 'pdf'
          ? `calculatie_${aggregation.project_id}.pdf`
          : format === 'excel'
            ? `calculatie_${aggregation.project_id}_excel.csv`
            : `calculatie_${aggregation.project_id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      trackOfferteGenerated(aggregation.project_id);
      setExportFeedback('Geweldig! Je export is succesvol gedownload.');
    } catch (e) {
      console.error(e);
      setFlowError(e instanceof Error ? e.message : 'Export mislukt.');
    } finally {
      setExporting(false);
    }
  };

  const handleGenerateDocument = async () => {
    if (!aggregation || !proj) return;
    setLoadingDoc(true);
    setFlowError(null);
    try {
      const doc = await api.generateDocument({
        project_id: aggregation.project_id,
        systems: aggregation.systems.map((s, i: number) => {
          const line = proj.systems?.[i];
          return {
            system_id: s.system_id,
            variant_id: s.variant_id,
            quantity: s.quantity,
            ...(line?.building_part?.trim() ? { building_part: line.building_part.trim() } : {}),
            ...(line?.floor?.trim() ? { floor: line.floor.trim() } : {}),
            ...(line?.room_number?.trim() ? { room_number: line.room_number.trim() } : {}),
          };
        }),
        output_type: 'client_quote',
        tenant_id: proj.tenant_id ?? 'default',
        project_type: proj.project_type ?? 'renovatie',
        building_type: proj.building_type ?? 'woning',
        index_year: proj.context?.index_year || undefined,
        region_id: proj.context?.region_id || undefined,
        vat_rate: exportVatRate,
        waste_factor: exportWasteFactor,
      });
      setGeneratedDoc(doc);
      setExportFeedback('Geweldig! Je offerte is klaar in het venster hieronder. Je kunt printen of PDF via de browser.');
    } catch (e) {
      console.error(e);
      setFlowError(e instanceof Error ? e.message : 'Offerte genereren mislukt.');
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleGenerateDocumentPdf = async () => {
    if (!aggregation || !proj) return;
    setLoadingDocPdf(true);
    setFlowError(null);
    try {
      const blob = await api.generateDocumentPdf({
        project_id: aggregation.project_id,
        systems: aggregation.systems.map((s, i: number) => {
          const line = proj.systems?.[i];
          return {
            system_id: s.system_id,
            variant_id: s.variant_id,
            quantity: s.quantity,
            ...(line?.building_part?.trim() ? { building_part: line.building_part.trim() } : {}),
            ...(line?.floor?.trim() ? { floor: line.floor.trim() } : {}),
            ...(line?.room_number?.trim() ? { room_number: line.room_number.trim() } : {}),
          };
        }),
        output_type: 'client_quote',
        tenant_id: proj.tenant_id ?? 'default',
        project_type: proj.project_type ?? 'renovatie',
        building_type: proj.building_type ?? 'woning',
        index_year: proj.context?.index_year || undefined,
        region_id: proj.context?.region_id || undefined,
        vat_rate: exportVatRate,
        waste_factor: exportWasteFactor,
      });
      const url = URL.createObjectURL(blob);
      const a = globalThis.document.createElement('a');
      a.href = url;
      a.download = `offerte_${aggregation.project_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      trackOfferteGenerated(aggregation.project_id);
    } catch (e) {
      console.error(e);
      setFlowError(e instanceof Error ? e.message : 'PDF-offerte mislukt.');
    } finally {
      setLoadingDocPdf(false);
    }
  };

  const handlePrintOfferte = () => {
    const doc = globalThis.document;
    const win = globalThis.window;
    const el = doc.getElementById('printable-offerte');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    win.setTimeout(() => {
      doc.body.classList.add('print-is-offerte');
      const afterPrint = () => {
        doc.body.classList.remove('print-is-offerte');
        win.removeEventListener('afterprint', afterPrint);
      };
      win.addEventListener('afterprint', afterPrint);
      win.print();
      /* Fallback als afterprint niet vuurt (sommige browsers) */
      win.setTimeout(() => doc.body.classList.remove('print-is-offerte'), 2000);
    }, 400);
  };

  const handleSaveContext = async () => {
    if (!projectId || !proj) return;
    const pc = normalizePostalCode(postalCode);
    if (pc && !/^\d{4}[A-Z]{2}$/.test(pc)) {
      setFlowError('Postcode ongeldig (gebruik formaat 1234AB)');
      return;
    }
    if (!customerType) {
      setFlowError('Kies een klanttype');
      return;
    }
    setFlowError(null);
    setContextSaving(true);
    try {
      let regionToSave = regionId.trim();
      if (pc && /^\d{4}[A-Z]{2}$/.test(pc)) {
        try {
          const s = await api.suggestRegionFromPostalCode(pc);
          regionToSave = s.region_id;
          setRegionId(s.region_id);
        } catch {
          /* handmatige regio blijft staan */
        }
      }
      await api.updateProject(
        projectId,
        {
          tenant_id: tenantIdForApi,
          project_type: projectType,
          building_type: buildingType,
          context: {
            postal_code: pc || undefined,
            region_id: regionToSave || undefined,
            index_year: proj.context?.index_year ?? DEFAULT_CALC_INDEX_YEAR,
            customer_type: customerType,
          },
        },
        { revisionSeq: proj.revision_seq ?? 1 }
      );
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setContextExpanded(false);
    } catch (e) {
      console.error(e);
      setFlowError(e instanceof Error ? e.message : 'Opslaan mislukt. Vernieuw het dossier en probeer opnieuw.');
    } finally {
      setContextSaving(false);
    }
  };

  /** Bij geldige postcode: regio ophalen en direct op het dossier opslaan. */
  const handleSuggestRegionFromPostal = async () => {
    if (!projectId || !proj) return;
    const pc = normalizePostalCode(postalCode);
    if (!pc || !/^\d{4}[A-Z]{2}$/.test(pc)) return;
    setFlowError(null);
    setRegionSuggestLoading(true);
    try {
      const s = await api.suggestRegionFromPostalCode(pc);
      setRegionId(s.region_id);
      const fresh = queryClient.getQueryData<ProjectRecord>(['project', projectId]) ?? proj;
      await api.updateProject(
        projectId,
        {
          tenant_id: fresh.tenant_id ?? tenantIdForApi,
          context: {
            ...fresh.context,
            postal_code: pc,
            region_id: s.region_id,
            index_year: fresh.context?.index_year ?? DEFAULT_CALC_INDEX_YEAR,
            customer_type: fresh.context?.customer_type ?? customerType ?? 'particulier',
          },
        },
        { revisionSeq: fresh.revision_seq ?? 1 }
      );
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    } catch (e) {
      setFlowError(e instanceof Error ? e.message : 'Regio kon niet automatisch worden bepaald.');
    } finally {
      setRegionSuggestLoading(false);
    }
  };

  const handleClearDrawingProposal = async () => {
    if (!projectId || !proj) return;
    setFlowError(null);
    try {
      await api.updateProject(projectId, {
        tenant_id: tenantIdForApi,
        clear_drawing_proposal: true,
      });
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setProposalRows(null);
    } catch (e) {
      setFlowError(e instanceof Error ? e.message : 'Voorstel wissen mislukt.');
    }
  };

  const handleApproveDrawingProposal = async (mode: ApproveDrawingProposalMode) => {
    if (!projectId || !proj?.drawing_proposal || !proposalRows?.length) return;
    setFlowError(null);
    setApproving(true);
    try {
      await api.updateProject(
        projectId,
        {
          tenant_id: tenantIdForApi,
          drawing_proposal: {
            ...proj.drawing_proposal,
            status: 'draft',
            proposed_systems: proposalRows,
          },
          approve_drawing_proposal: true,
          approve_drawing_proposal_mode: mode,
        },
        { revisionSeq: proj.revision_seq ?? 1 }
      );
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setGeneratedDoc(null);
    } catch (e) {
      setFlowError(e instanceof Error ? e.message : 'Goedkeuren mislukt. Probeer opnieuw of wis het voorstel.');
    } finally {
      setApproving(false);
    }
  };

  const handleSaveBoq = async () => {
    if (!projectId || !proj) return;
    setFlowError(null);
    setBoqSaving(true);
    try {
      await api.updateProject(
        projectId,
        {
          tenant_id: tenantIdForApi,
          systems: boqDraft.map((r) => ({
            system_id: r.system_id,
            variant_id: r.variant_id,
            quantity: r.quantity,
            ...(r.building_part?.trim() ? { building_part: r.building_part.trim() } : {}),
            ...(r.floor?.trim() ? { floor: r.floor.trim() } : {}),
            ...(r.room_number?.trim() ? { room_number: r.room_number.trim() } : {}),
            ...(r.line_id ? { line_id: r.line_id } : {}),
          })),
        },
        { revisionSeq: proj.revision_seq ?? 1 }
      );
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    } catch (e) {
      console.error(e);
      setFlowError(e instanceof Error ? e.message : 'Regels opslaan mislukt.');
    } finally {
      setBoqSaving(false);
    }
  };

  const regionDisplayLabel = useMemo(() => {
    const id = regionId.trim();
    if (!id) return 'Nog niet ingesteld';
    const r = regionsData?.regions?.find((x) => x.region_id === id);
    return r?.label ?? id;
  }, [regionsData?.regions, regionId]);

  const customerLabel =
    CUSTOMER_TYPE_OPTIONS.find((o) => o.id === customerType)?.label ?? customerType;

  const handleCopyProjectName = useCallback(async () => {
    const saved =
      (proj?.metadata?.name?.trim() || proj?.project_id || '') as string;
    const text = nameEditing ? nameDraft.trim() : saved;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setNameCopyFlash(true);
      window.setTimeout(() => setNameCopyFlash(false), 2000);
    } catch {
      setFlowError('Kopiëren niet gelukt (browsertoestemming?).');
    }
  }, [nameDraft, nameEditing, proj?.metadata?.name, proj?.project_id]);

  const handleSaveProjectName = useCallback(async () => {
    if (!projectId || !proj) return;
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setFlowError('Vul een projectnaam in.');
      return;
    }
    setNameSaving(true);
    setFlowError(null);
    try {
      await api.updateProject(
        projectId,
        {
          tenant_id: tenantIdForApi,
          metadata: { ...proj.metadata, name: trimmed },
        },
        { revisionSeq: proj.revision_seq ?? 1 }
      );
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setNameEditing(false);
    } catch (e) {
      setFlowError(e instanceof Error ? e.message : 'Naam opslaan mislukt.');
    } finally {
      setNameSaving(false);
    }
  }, [nameDraft, projectId, proj, queryClient, tenantIdForApi]);

  /** Must run before any conditional return — same hook order every render (React #310). */
  const projectSeo = useMemo(() => {
    const m = location.pathname.match(/^\/projects\/[^/]+\/([a-z]+)/);
    const rawTab = m?.[1] ?? 'gegevens';
    const tab = rawTab === 'resultaten' ? 'resultaat' : rawTab;
    const pid = projectId ?? '';
    const path = `/projects/${pid}/${tab}`;
    const nameForMeta = (proj?.metadata?.name?.trim() || proj?.project_id || pid || 'Dossier') as string;
    const safeName = nameForMeta.slice(0, 80);
    const byTab: Record<string, { title: string; description: string }> = {
      gegevens: {
        title: `Dossier ${safeName} — gegevens`,
        description: `Projectgegevens, context en documenten voor “${safeName}”. Woningtype, regio en tekening — CalqTool calculatiedossier.`,
      },
      posten: {
        title: `Dossier ${safeName} — posten`,
        description: `Begrotingsposten en prijsboek voor “${safeName}”. Voeg systemen toe, bewerk hoeveelheden en bewaar regels — CalqTool.`,
      },
      resultaat: {
        title: `Dossier ${safeName} — resultaat`,
        description: `Totalen en kostenopbouw voor “${safeName}”. Bekijk materialen, arbeid en uitkomsten per deel — CalqTool.`,
      },
      rapportage: {
        title: `Dossier ${safeName} — rapportage`,
        description: `Export begroting, calculatie en offerte voor “${safeName}”. CSV, Excel, PDF — instelbare detailgraad — CalqTool.`,
      },
    };
    const meta = byTab[tab] ?? byTab.gegevens;
    return { path, ...meta };
  }, [location.pathname, projectId, proj?.metadata?.name, proj?.project_id]);

  if (loadingProject || !projectId) {
    return (
      <div className="flex min-h-[45vh] flex-col items-center justify-center gap-3 text-text-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
        <p className="text-sm">Dossier laden…</p>
      </div>
    );
  }

  if (error || !proj) {
    return (
      <div className="animate-page-enter">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          <p className="font-semibold">Dossier niet gevonden</p>
          <p className="mt-1 text-sm opacity-90">Controleer de link of ga terug naar het dashboard.</p>
        </div>
      </div>
    );
  }

  const savedDisplayName = (proj.metadata?.name?.trim() || proj.project_id) as string;
  const displayName = savedDisplayName;

  const outletContext: ProjectDetailOutletContext = {
    projectId: projectId!,
    proj,
    displayName,
    queryClient,
    tenantIdForApi,
    indexForPricing,
    flowError,
    setFlowError,
    projectType,
    setProjectType,
    buildingType,
    setBuildingType,
    postalCode,
    setPostalCode,
    regionId,
    setRegionId,
    customerType,
    setCustomerType,
    contextExpanded,
    setContextExpanded,
    contextSaving,
    regionSuggestLoading,
    typesLoading,
    typesLoadError,
    loadReferenceTypes,
    projectTypeOptionsMerged,
    buildingTypeOptionsMerged,
    projectTypeLabel,
    buildingTypeLabel,
    regionDisplayLabel,
    customerLabel,
    handleSaveContext,
    handleSuggestRegionFromPostal,
    handleClearDrawingProposal,
    savedSystemsCount,
    canAccessResultTabs,
    hasSystems,
    pendingDrawingApproval,
    proposalRows,
    setProposalRows,
    approving,
    handleApproveDrawingProposal,
    boqDraft,
    setBoqDraft,
    boqUndo,
    boqRedo,
    boqCanUndo,
    boqCanRedo,
    boqSaving,
    handleSaveBoq,
    aggregation,
    aggregationLoading,
    aggregationError,
    expandedBreakdown,
    setExpandedBreakdown,
    generatedDoc,
    loadingDoc,
    exporting,
    exportModalOpen,
    setExportModalOpen,
    exportVatRate,
    setExportVatRate,
    handleExport,
    handleGenerateDocument,
    handleGenerateDocumentPdf,
    handlePrintOfferte,
    loadingDocPdf,
    journeyTabStep,
    customerUxProfile,
    exportWasteFactor,
    setExportWasteFactor,
    exportCsvDelimiter,
    setExportCsvDelimiter,
    exportIncludeDisclaimer,
    setExportIncludeDisclaimer,
    exportIncludePerDeelBegroting,
    setExportIncludePerDeelBegroting,
    exportIncludeCostSummary,
    setExportIncludeCostSummary,
    exportIncludeRegeldetail,
    setExportIncludeRegeldetail,
    exportIncludeCo2,
    setExportIncludeCo2,
    exportFeedback,
    setExportFeedback,
    detailProfileHint,
    inputClass,
    btnPrimary,
    btnPrimaryLg,
    btnSecondary,
  };

  return (
    <div className="animate-page-enter pb-10">
      <SeoMeta
        path={projectSeo.path}
        title={projectSeo.title}
        description={projectSeo.description}
        noindex
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs
          items={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: displayName },
          ]}
        />

        <div className="relative mb-6 overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-br from-primary/[0.09] via-surface-card to-surface-card p-5 shadow-sm md:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-4">
              <div className="hidden shrink-0 rounded-xl bg-primary/15 p-3 text-primary sm:block">
                <FolderKanban className="h-8 w-8" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Dossier</p>
                {nameEditing ? (
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                    <input
                      type="text"
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      className={`${inputClass} max-w-xl`}
                      placeholder="Projectnaam"
                      aria-label="Projectnaam bewerken"
                      autoFocus
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={nameSaving}
                        onClick={() => void handleSaveProjectName()}
                        className={`${btnPrimary} min-h-[40px] px-4 py-2 text-sm`}
                      >
                        {nameSaving ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            Opslaan…
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4" aria-hidden />
                            Opslaan
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        disabled={nameSaving}
                        onClick={() => {
                          setNameEditing(false);
                          setNameDraft('');
                          setFlowError(null);
                        }}
                        className={`${btnSecondary} min-h-[40px] px-4 py-2 text-sm`}
                      >
                        <X className="h-4 w-4" aria-hidden />
                        Annuleren
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-0.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                    <h1 className="text-2xl font-bold tracking-tight text-text md:text-3xl">{savedDisplayName}</h1>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={handleCopyProjectName}
                        className={`${btnSecondary} min-h-[40px] gap-1.5 px-3 py-1.5 text-xs sm:text-sm`}
                        title="Projectnaam kopiëren"
                      >
                        <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                        {nameCopyFlash ? 'Gekopieerd' : 'Kopiëren'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setNameDraft((proj.metadata?.name?.trim() || proj.project_id) ?? '');
                          setNameEditing(true);
                          setFlowError(null);
                        }}
                        className={`${btnSecondary} min-h-[40px] gap-1.5 px-3 py-1.5 text-xs sm:text-sm`}
                      >
                        <Pencil className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                        Naam wijzigen
                      </button>
                    </div>
                  </div>
                )}
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-text-muted">
                  <strong className="text-text">Tabs</strong> blijven bij scrollen plakken onder de menubalk; in de
                  zijkolom kun je dezelfde <strong className="text-text">vier stappen aanklikken</strong>. Actieve stap
                  is blauw gemarkeerd.
                </p>
              </div>
            </div>
            {pendingDrawingApproval ? (
              <span className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full border border-amber-300 bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-950 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-100 sm:self-center">
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                Voorstel te keuren
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <aside className="order-2 w-full shrink-0 lg:order-1 lg:sticky lg:top-20 lg:z-[5] lg:max-w-[18rem]">
            <ProjectJourneySteps
              variant="empty"
              alignWithTabs
              currentStep={journeyTabStep}
              profileHint={detailProfileHint}
              compact
              className="lg:shadow-md"
              tabStepLinks={dossierJourneyTabLinks}
            />
          </aside>

          <div className="order-1 min-w-0 flex-1 lg:order-2">
            <nav
              className="sticky top-20 z-[35] -mx-1 mb-5 flex flex-wrap gap-2 border-b border-border/80 bg-surface-card/95 px-1 pb-2 pt-2 shadow-sm backdrop-blur-md supports-[backdrop-filter]:bg-surface-card/90"
              aria-label="Dossier-stappen"
            >
              <DossierTabLink to={`/projects/${projectId}/gegevens`} end>
                1 · Gegevens
              </DossierTabLink>
              <DossierTabLink to={`/projects/${projectId}/posten`}>2 · Posten</DossierTabLink>
              <DossierTabLink to={`/projects/${projectId}/resultaat`} disabled={!canAccessResultTabs}>
                3 · Resultaat
              </DossierTabLink>
              <DossierTabLink to={`/projects/${projectId}/rapportage`} disabled={!canAccessResultTabs}>
                4 · Rapportage
              </DossierTabLink>
            </nav>

            {flowError && (
              <div
                className="mb-6 flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200 sm:flex-row sm:items-center sm:justify-between"
                role="alert"
              >
                <span>{flowError}</span>
                <button
                  type="button"
                  onClick={() => setFlowError(null)}
                  className="shrink-0 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-900 hover:bg-red-100 dark:border-red-800 dark:bg-red-950 dark:text-red-100 dark:hover:bg-red-900/50"
                >
                  Sluiten
                </button>
              </div>
            )}

            <Outlet context={outletContext} />
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
}
